import { useCallback, useMemo, useState } from 'react';
import type { LectureData, Question } from '../../types';
import type { QuestionAggregation, UserRankingInfo } from '../../stores/lectureStore';
import { TestResultList } from './TestResultList';
import {
  getDisplayRankingTier,
  getRankingTierComment,
  getRankingCommentFromApi,
} from './testSummaryRanking';
import {
  getCourseShareUrl,
  buildShareText,
  buildXShareUrl,
  buildFacebookShareUrl,
  buildLineShareUrl,
} from '../../utils/snsShare';
import { isCorrectAnswer } from '../../utils/answerJudge';

export interface TestSummaryProps {
  lectureData: LectureData;
  questions: Question[];
  selectedChoices: Record<number, number | null>;
  questionAggregations?: Record<number, QuestionAggregation>;
  isTrialUser?: boolean;
  isArchive?: boolean;
  userRanking?: UserRankingInfo | null;
  userId?: string;
  lectureId?: string;
}

/**
 * TestSummary - 講義終了後の結果サマリーを表示
 */
export function TestSummary({
  lectureData,
  questions,
  selectedChoices,
  questionAggregations = {},
  isTrialUser = false,
  isArchive = false,
  userRanking = null,
  userId,
  lectureId,
}: TestSummaryProps) {
  // フローティングトースト通知
  const [toast, setToast] = useState<{
    visible: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({ visible: false, type: 'success', message: '' });

  const showToast = useCallback(
    (type: 'success' | 'error' | 'info', message: string, durationMs = 2500) => {
      setToast({ visible: true, type, message });
      setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), durationMs);
    },
    [],
  );

  // 正答率と正解数を計算
  const summary = useMemo(() => {
    // 未出題（not_started）の問題は分母に含めない（AGG-1対応）。
    // 講義終了時の /questions/ended は questions.json 上の全問題（未出題含む）を返すため、
    // status を見ずに全問を分母にすると「登録10問・出題5問・全問正解」の受講生に
    // 正答率50%と表示され、バックエンドのランキング（出題済みのみが母数）と矛盾していた
    const scoredQuestions = questions.filter(
      (q) => q.format_type === 'question' && q.status !== 'not_started',
    );
    
    // 正誤判定はバックエンド（最初の is_correct のみ正解）と定義を統一（AGG-10対応）
    const correctCount = scoredQuestions.filter((q) =>
      isCorrectAnswer(q, selectedChoices[q.id]),
    ).length;
    const totalCount = scoredQuestions.length;
    const percentage = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
    
    // ランキング情報がAPIから取得されている場合はそれを優先、なければモック実装を使用
    let displayRankingTier: 20 | 40 | null = null;
    let rankingComment: string | null = null;
    
    if (userRanking && userRanking.tier !== undefined) {
      // APIから取得したランキング情報を使用
      displayRankingTier = userRanking.tier;
      // コメントはフロントエンド側で生成（APIのcommentは使用しない）
      rankingComment = getRankingCommentFromApi(userRanking.tier, userRanking.total_users);
      console.log('[TestSummary] APIから取得したランキング情報を使用:', { 
        tier: displayRankingTier, 
        totalUsers: userRanking.total_users,
        comment: rankingComment 
      });
    } else {
      // モック実装を使用（後方互換性のため）
      displayRankingTier = getDisplayRankingTier(percentage, isTrialUser);
      rankingComment = displayRankingTier != null ? getRankingTierComment(displayRankingTier) : null;
      console.log('[TestSummary] モック実装のランキング情報を使用:', { tier: displayRankingTier, comment: rankingComment });
    }

    return {
      correctCount,
      totalCount,
      percentage,
      displayRankingTier,
      rankingComment,
    };
  }, [questions, selectedChoices, isTrialUser, userRanking]);

  // SNS共有の共通データを導出
  const courseDataType = lectureData.course?.data_type ?? null;
  const courseName = lectureData.course?.name ?? null;
  // 共有先URL（フォーサイト公式講座ページ）
  const courseUrl = getCourseShareUrl(courseDataType);
  // X・コピー用：ハッシュタグあり
  const shareText = buildShareText(courseName, summary.displayRankingTier);
  // LINE用：トークではハッシュタグがリンクにならないため除く
  const lineShareText = buildShareText(courseName, summary.displayRankingTier, false);

  // クリップボードコピーのヘルパー
  const copyToClipboard = useCallback(
    (text: string, onSuccess: () => void, onError: () => void) => {
      const execFallback = () => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
          onSuccess();
        } catch {
          onError();
        }
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(onSuccess).catch(execFallback);
      } else {
        execFallback();
      }
    },
    [],
  );

  // SNS共有ボタンのハンドラ
  const handleShare = useCallback(
    (type: string) => {
      switch (type) {
        case 'x': {
          window.open(
            buildXShareUrl(shareText, courseUrl),
            '_blank',
            'width=600,height=400',
          );
          break;
        }
        case 'facebook': {
          window.open(
            buildFacebookShareUrl(courseUrl),
            '_blank',
            'width=600,height=400',
          );
          break;
        }
        case 'line': {
          window.open(
            buildLineShareUrl(courseUrl, lineShareText),
            '_blank',
            'width=600,height=600',
          );
          break;
        }
        case 'copy': {
          const payload = `${shareText}\n${courseUrl}`;
          copyToClipboard(
            payload,
            () => showToast('success', 'テキストをコピーしました ✓'),
            () => showToast('error', 'コピーに失敗しました'),
          );
          break;
        }
        default:
          break;
      }
    },
    [shareText, lineShareText, courseUrl, copyToClipboard, showToast],
  );

  // アーカイブ時はTestResultListのみ表示（TestSummaryのカード枠・ヘッダー不要）
  if (isArchive) {
    return (
      <div className="test-summary">
        {isTrialUser && (
          <div className="test-trial-notice" role="alert">
            <div className="test-trial-notice-icon" aria-hidden="true">⚠</div>
            <div className="test-trial-notice-text">
              試用ユーザーの方は、解答の記録・集計はされません。
            </div>
          </div>
        )}
        <TestResultList 
          questions={questions} 
          selectedChoices={selectedChoices} 
          questionAggregations={questionAggregations} 
          isArchive 
          {...(userId !== undefined && { userId })}
          {...(lectureId !== undefined && { lectureId })}
        />
      </div>
    );
  }

  return (
    <div className="test-summary">
      {/* フローティングトースト通知 */}
      {toast.visible && (
        <div
          className={`test-share-toast test-share-toast--${toast.type}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' && (
            <svg className="test-share-toast-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
            </svg>
          )}
          {toast.type === 'error' && (
            <svg className="test-share-toast-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd" />
            </svg>
          )}
          {toast.type === 'info' && (
            <svg className="test-share-toast-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      <div className="test-summary-card">
        <div className="test-summary-header">
          <h2 className="test-summary-title">結果サマリー</h2>
          <div className="test-summary-share-buttons">
            <button
              type="button"
              className="test-share-btn"
              data-share="copy"
              aria-label="コピー"
              title="コピー"
              onClick={() => handleShare('copy')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M7.5 3.75A2.25 2.25 0 0 0 5.25 6v10.5A2.25 2.25 0 0 0 7.5 18.75H9a.75.75 0 0 0 0-1.5H7.5a.75.75 0 0 1-.75-.75V6a.75.75 0 0 1 .75-.75H15a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 0 1.5 0V6A2.25 2.25 0 0 0 15 3.75H7.5Zm6 4.5A2.25 2.25 0 0 0 11.25 10.5V21A2.25 2.25 0 0 0 13.5 23.25H19.5A2.25 2.25 0 0 0 21.75 21V10.5A2.25 2.25 0 0 0 19.5 8.25h-6Zm-.75 2.25a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 .75.75V21a.75.75 0 0 1-.75.75h-6a.75.75 0 0 1-.75-.75V10.5Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button
              type="button"
              className="test-share-btn"
              data-share="facebook"
              aria-label="Facebook"
              title="Facebook"
              onClick={() => handleShare('facebook')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
              </svg>
            </button>
            <button
              type="button"
              className="test-share-btn"
              data-share="x"
              aria-label="X"
              title="X"
              onClick={() => handleShare('x')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
              </svg>
            </button>
            <button
              type="button"
              className="test-share-btn"
              data-share="line"
              aria-label="LINE"
              title="LINE"
              onClick={() => handleShare('line')}
            >
              {/* LINE公式ロゴ */}
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
              </svg>
            </button>
          </div>
        </div>
        {/* 試用ユーザー通知 */}
        {isTrialUser && (
          <div className="test-trial-notice" role="alert">
            <div className="test-trial-notice-icon" aria-hidden="true">⚠</div>
            <div className="test-trial-notice-text">
              試用ユーザーの方は、解答の記録・集計はされません。
            </div>
          </div>
        )}

        {/* KPI行（正答率・ランキング参考） */}
        <div
          className={
            summary.rankingComment != null
              ? 'test-summary-kpi-row has-ranking'
              : 'test-summary-kpi-row'
          }
        >
          <div className="test-summary-kpi" aria-label="正答率">
            <div className="test-summary-metric-label">正答率</div>
            <div className="test-summary-metric-value">{summary.percentage}%</div>
            <div className="test-summary-metric-sub">
              {summary.totalCount}問中{summary.correctCount}問正解
            </div>
          </div>
          {summary.rankingComment != null && (
            <div className="test-summary-ranking" aria-label="ランキング（参考）">
              <div className="test-summary-ranking-label">ランキング（参考）</div>
              {summary.displayRankingTier != null && (
                <div className="test-summary-ranking-value">
                  上位{summary.displayRankingTier}%
                </div>
              )}
              <div className="test-summary-ranking-comment">{summary.rankingComment}</div>
            </div>
          )}
        </div>

        {/* アンケートカード（survey_url がある場合のみ） */}
        {!isTrialUser && lectureData.survey_url && (
          <div className="test-summary-survey-card">
            <div className="test-summary-survey-text">
              <div className="test-summary-survey-title">アンケートにご協力お願いします</div>
              <div className="test-summary-survey-sub">講義後1週間受付</div>
            </div>
            <a
              className="test-summary-survey-cta"
              href={lectureData.survey_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              アンケートに回答する
            </a>
          </div>
        )}
      </div>

      {/* テスト結果一覧（test-summary-card の外に独立して配置） */}
      <div className="test-summary-details">
        <TestResultList 
          questions={questions} 
          selectedChoices={selectedChoices} 
          questionAggregations={questionAggregations} 
          {...(userId !== undefined && { userId })}
          {...(lectureId !== undefined && { lectureId })}
        />
      </div>
    </div>
  );
}
